<script setup lang="ts">
import { computed } from 'vue';
import { BCard, BSkeletonLoader } from '@/design-system';

type WarehouseStatType = {
    label: string;
    value: string | number;
    variant?: 'in' | 'out' | 'default';
};

type PropsType = {
    name?: string;
    stats?: WarehouseStatType[];
    status?: 'online' | 'offline' | 'warning';
    isLoading?: boolean;
    clickable?: boolean;
};

const props = withDefaults(defineProps<PropsType>(), {
    name: '',
    stats: () => [],
    status: 'online',
    isLoading: false,
    clickable: false,
});

const cardClass = computed(() => {
    const classes = ['b-warehouse-card'];
    if (props.clickable) {
        classes.push('b-warehouse-card--clickable');
    }

    return classes.join(' ');
});

const statusColor = computed(() => {
    const colorMap = {
        online: 'accent-green-dashboard',
        offline: 'accent-red-dashboard',
        warning: 'accent-orange',
    };

    return colorMap[props.status];
});

const getStatValueClass = (variant?: 'in' | 'out' | 'default') => {
    const classes = ['b-warehouse-card__stat-value'];
    if (variant === 'in') {
        classes.push('b-warehouse-card__stat-value--in');
    } else if (variant === 'out') {
        classes.push('b-warehouse-card__stat-value--out');
    }

    return classes.join(' ');
};

const formatStatValue = (value: string | number, variant?: 'in' | 'out' | 'default') => {
    const stringValue = typeof value === 'number' ? value.toString() : value;
    if (variant === 'in') {
        return `+${stringValue}`;
    }
    if (variant === 'out') {
        return `-${stringValue}`;
    }

    return stringValue;
};
</script>

<template>
    <BCard
        :class="cardClass"
        class="pa-0"
        text-class="pa-0"
        variant="flat"
    >
        <BSkeletonLoader
            type="list-item-avatar-three-line"
            :loading="isLoading"
        >
            <div class="b-warehouse-card__content">
                <div class="b-warehouse-card__header">
                    <div class="b-warehouse-card__name">
                        {{ name }}
                    </div>
                    <div
                        class="b-warehouse-card__status"
                        :style="{ backgroundColor: `rgb(var(--v-theme-${statusColor}))` }"
                    />
                </div>

                <div class="b-warehouse-card__stats">
                    <div
                        v-for="(stat, index) in stats"
                        :key="index"
                        class="b-warehouse-card__stat"
                    >
                        <span class="b-warehouse-card__stat-label">{{ stat.label }}</span>
                        <span :class="getStatValueClass(stat.variant)">
                            {{ formatStatValue(stat.value, stat.variant) }}
                        </span>
                    </div>
                </div>
            </div>
        </BSkeletonLoader>
    </BCard>
</template>

<style scoped lang="scss">
@use '@/design-system/styles/dashboard-animations';

.b-warehouse-card {
    --content-padding-inline: 30px;
    --content-padding-block: 20px;
    border: 1px solid rgb(204, 225, 235);
    border-radius: 8px;
    height: 100%;
    transition: box-shadow 0.2s ease;
}

.b-warehouse-card :deep(.v-card__overlay) {
    --v-hover-opacity: 0;
}

.b-warehouse-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.b-warehouse-card__content {
    padding-block: var(--content-padding-block);
    padding-inline: var(--content-padding-inline);
}

.b-warehouse-card__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
}

.b-warehouse-card__name {
    font-size: 13px;
    font-weight: 600;
    color: rgb(var(--v-theme-on-surface));
}

.b-warehouse-card__status {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
}

.b-warehouse-card__stats {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.b-warehouse-card__stat {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.b-warehouse-card__stat-label {
    font-size: 11px;
    color: rgba(var(--v-theme-on-surface), 0.6);
}

.b-warehouse-card__stat-value {
    font-size: 13px;
    font-weight: 600;
    color: rgb(var(--v-theme-on-surface));
}

.b-warehouse-card__stat-value--in {
    color: rgb(var(--v-theme-accent-green-dashboard));
}

.b-warehouse-card__stat-value--out {
    color: rgb(var(--v-theme-accent-orange));
}
</style>
